import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import React from 'react';
import { captureError } from '../utils/capture-error';
import { isAgt } from '../utils/is-agent';

/** Subset of the Freshchat widget API used here. */
interface FcWidget {
  open: () => void;
  setExternalId: (id: string) => void;
  user: {
    setFirstName: (name: string) => void;
    setEmail: (email: string) => void;
    setProperties: (properties: Record<string, string>) => void;
  };
}

/** How long to wait for the Freshchat script to define `window.fcWidget`. */
const WIDGET_WAIT_MS = 10000;

/**
 * Resolves with `window.fcWidget` once the Freshchat script has loaded, or
 * rejects after `WIDGET_WAIT_MS` (blocked script, offline, domain not allowed).
 */
const waitForWidget = () =>
  new Promise<FcWidget>((resolve, reject) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const widget = (window as unknown as { fcWidget?: FcWidget }).fcWidget;
      if (widget) {
        window.clearInterval(timer);
        resolve(widget);
      } else if (Date.now() - started > WIDGET_WAIT_MS) {
        window.clearInterval(timer);
        reject(new Error('Freshchat did not load'));
      }
    }, 200);
  });

/** Where the visitor's details are kept, in this browser. */
const STORAGE_KEY = 'dmd-chat-details';

interface ChatDetails {
  name: string;
  email: string;
  agentNumber: string;
}

/** Details entered earlier in this browser, or blanks (storage can be blocked). */
const readSavedDetails = (): ChatDetails => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        name: String(parsed.name ?? ''),
        email: String(parsed.email ?? ''),
        agentNumber: String(parsed.agentNumber ?? ''),
      };
    }
  } catch {
    // Unavailable or malformed: fall through to the empty form
  }
  return { name: '', email: '', agentNumber: '' };
};

const saveDetails = (details: ChatDetails) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(details));
  } catch {
    // Not saving only means the form is shown again after a reload
  }
};

/**
 * The chat button, shown before and after sign-in. Clicking it collects name,
 * email and agent number, hands them to Freshchat, then opens the widget so the
 * visitor picks a topic with that information already attached.
 *
 * Freshchat's own launcher is hidden by `fcWidgetMessengerConfig` in
 * `public/index.html`, so this is the only chat button on screen.
 */
export function ChatLauncher() {
  // State
  const saved = React.useMemo(readSavedDetails, []);
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState(saved.name);
  const [email, setEmail] = React.useState(saved.email);
  const [agentNumber, setAgentNumber] = React.useState(saved.agentNumber);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  // Agents may enter a DMD number or an NPN, so any non-empty value will do
  const canSubmit =
    name.trim().length > 0 && emailValid && agentNumber.trim().length > 0;

  /**
   * Opens chat straight away when the visitor has already given their details
   * (this browser), and shows the form otherwise.
   */
  const handleLauncherClick = () => {
    if (canSubmit && !submitting) {
      startChat();
    } else {
      setOpen(true);
    }
  };

  /** Sets the visitor's details on the Freshchat contact, then opens chat. */
  const startChat = async () => {
    setSubmitting(true);
    setError('');
    try {
      const widget = await waitForWidget();
      const id = agentNumber.trim().toUpperCase();
      // Only a DMD number identifies the contact; an NPN is just a property
      if (isAgt(id)) {
        widget.setExternalId(id);
      }
      widget.user.setFirstName(name.trim());
      widget.user.setEmail(email.trim());
      // The contact shows this as `naa_number`. We don't know which internal
      // name the property was created under, and Freshchat ignores names that
      // don't exist, so send the likely spellings. Trim to the one that works.
      widget.user.setProperties({
        naa_number: id,
        cf_naa_number: id,
        NAA_Number: id,
      });
      saveDetails({ name: name.trim(), email: email.trim(), agentNumber: id });
      setOpen(false);
      widget.open();
    } catch (err) {
      captureError({ data: { error: err } });
      setError('Chat didn’t load. Please try again.');
      // Show the form so the error is visible when chat started from a click
      setOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <Box
        component="button"
        type="button"
        aria-label="Chat with us"
        onClick={handleLauncherClick}
        sx={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 999999,
          width: 56,
          height: 56,
          p: 0,
          border: 'none',
          borderRadius: '50%',
          backgroundColor: '#2b5bd6',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 12px #0000003d',
        }}
      >
        <ChatBubbleIcon sx={{ fontSize: 26 }} />
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Chat with us</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            id="chat-details"
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
              if (canSubmit && !submitting) {
                startChat();
              }
            }}
            sx={{ display: 'grid', gap: 2, pt: 1 }}
          >
            <Box component="p" sx={{ m: 0 }}>
              Tell us a bit about yourself, then choose a topic.
            </Box>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoFocus
              required
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={email !== '' && !emailValid}
              helperText={
                email !== '' && !emailValid ? 'Enter a valid email.' : ''
              }
              required
            />
            <TextField
              label="Agent Number"
              value={agentNumber}
              onChange={(event) => setAgentNumber(event.target.value)}
              helperText="Your DMD number, or your NPN if you don’t know it."
              required
            />
            {error && (
              <Box role="alert" sx={{ color: 'error.main' }}>
                {error}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="chat-details"
            variant="contained"
            disabled={!canSubmit || submitting}
          >
            Start chat
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
